import _ from 'underscore';
import React, { useContext, useState } from 'react';
import {
  Image, Spinner, makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';

import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Creative } from './types';
import { Placements } from './consts';
import { VariantCount } from './VariantCount';

const getPreview = (creative: Creative) => {
  const { height, width } = Placements[creative.previewType];

  return {
    src: creative.previewSrc,
    width,
    height,
  };
};

const transitionStyle = shorthands.transition('all', '0.2s', 'cubic-bezier(0.25, 0.8, 0.25, 1)');

const useCreativeCardStyles = makeStyles({
  root: {
    position: 'relative',
    zIndex: 1,

    ...shorthands.border('1px', 'solid', 'rgb(209, 209, 209)'),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.overflow('hidden'),

    '& .spinner-layer': {
      opacity: 0,
    },
    '& .preview-layer': {
      opacity: 1,
      ...transitionStyle,
    },
    '& .gradient-layer': {
      opacity: 0,
      ...transitionStyle,
    },
    '& .count-layer': {
      opacity: 1,
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
      ...transitionStyle,
    },
  },
  rootLoading: {
    '& .spinner-layer': { opacity: 1 },
    '& .preview-layer': { opacity: 0 },
    '& .gradient-layer': { opacity: 0 },
    '& .count-layer': { opacity: 0 },
  },
  rootHover: {
    ':hover': {
      '& .gradient-layer': {
        opacity: 0.8,
      },
    },
  },
  spinnerLayer: {
    position: 'absolute',
    pointerEvents: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  previewLayer: {
    position: 'absolute',
    pointerEvents: 'none',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    ...shorthands.overflow('hidden'),
  },
  gradientLayer: {
    position: 'absolute',
    pointerEvents: 'none',
    top: 0,
    height: '100%',
    width: '100%',
    backgroundImage: 'linear-gradient(180deg, rgba(49, 34, 34, 0.2) 44.79%, rgba(49, 34, 34, 0.8) 76.04%, rgba(49, 34, 34, 1) 100%)',
  },
  countLayer: {
    position: 'absolute',
    pointerEvents: 'none',
    bottom: 0,
    width: '100%',
  },
});

interface IPreviewProps {
  creative: Creative;
  onLoad: () => void;
}

export const Preview: React.FC<IPreviewProps> = ({ creative, onLoad }) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const classes = useCreativeCardStyles();

  const { src, width, height } = getPreview(creative);

  const [loading, setLoading] = useState(true);

  const loadingComplete = () => {
    setLoading(false);
    onLoad();
  };

  return (
    <div
      className={mergeClasses('creative-preview', classes.root, !loading && classes.rootHover, loading && classes.rootLoading)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <div className={mergeClasses('spinner-layer', classes.spinnerLayer)}>
        {loading && <Spinner aria-label={_TL_('Loading')} />}
      </div>
      <div className={mergeClasses('preview-layer', classes.previewLayer)}>
        <Image
          block
          alt={i18n.getString(_TL_('Creative preview'))}
          src={src}
          onLoad={loadingComplete}
          onError={loadingComplete}
        />
      </div>
      <div className={mergeClasses('gradient-layer', classes.gradientLayer)} />
      <div className={mergeClasses('count-layer', classes.countLayer)}>
        <VariantCount count={creative.placements.length} />
      </div>
    </div>
  );
};
